import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, FormField, Modal, TextInput } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { ApiError } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type {
  DispatchAssignmentCommandResponse,
  DispatchEndOutcome,
  DispatchProblemResolution,
  DispatchProblemResolveResponse,
} from "@/lib/api/dispatchTypes";
import { useErrorMessage } from "@/shared/errors";
import { usePrivateToast } from "@/shared/usePrivateToast";
import {
  clockTime,
  dispatchActions,
  dispatchChip,
  isolate,
  problemReason,
  type BillDispatch,
  type KnownProblemReason,
} from "@/modules/pos/lib/dispatchBoard";
import { dispatchQueryKeys } from "@/modules/pos/lib/useDispatchBoard";
import { useDispatchCommand } from "@/modules/pos/lib/useDispatchCommand";
import { DispatchStatusChip } from "./DispatchStatusChip";

/** The server's bound on a note (`DISPATCH_NOTE_MAX_LENGTH`). */
export const DISPATCH_NOTE_MAX = 500;

/** The refusals this sheet can meet (plan D-32), each in the till's words. */
const REFUSALS = {
  DISPATCH_PROBLEM_NOT_OPEN: "errorProblemNotOpen",
  DISPATCH_INVALID_TRANSITION: "errorInvalidTransition",
  DISPATCH_ASSIGNMENT_NOT_FOUND: "errorNotFound",
  DISPATCH_COMMAND_ID_CONFLICT: "errorConflict",
} as const;

const REASON_KEY: Record<KnownProblemReason, "reasonNoAnswer" | "reasonWrongAddress" | "reasonRefused" | "reasonMissingOrDamaged" | "reasonOther"> = {
  NO_ANSWER: "reasonNoAnswer",
  WRONG_ADDRESS: "reasonWrongAddress",
  REFUSED: "reasonRefused",
  MISSING_OR_DAMAGED: "reasonMissingOrDamaged",
  OTHER: "reasonOther",
};

/** A step that needs a second tap before it is sent (irreversible). */
type Arming =
  | { kind: "resolve"; resolution: "RETURN_TO_STORE" | "CANCELLED" }
  | { kind: "end"; outcome: DispatchEndOutcome }
  | null;

/**
 * «متابعة التوصيل» — a delivery bag past the offer (driver backend B3 · P14;
 * plan D-15, tasks T10.5 / T14.4).
 *
 * 1. THE DRIVER'S REPORT (T5), while one is open: the reason, the driver's
 *    note and when; the till answers with RETRY (the driver carries on),
 *    RETURN_TO_STORE (the bag comes back, nothing collected) or CANCELLED
 *    (the delivery ends) — both on a second tap, because neither can be
 *    undone — with an
 *    optional message the driver sees.
 * 2. END THE DELIVERY FROM THE TILL (T16), past pickup — on the way, arrived
 *    or coming back — for the bag whose driver can no longer move it: it came
 *    back (RETURNED) or it is called off (CANCELLED), always on a second tap.
 *    The note is offered only while a report is open (G10 L1): the server
 *    keeps it as that report's resolution note and has nowhere else to put it.
 *
 * Neither touches the bill (D-15): a cancelled or returned bag is then handled
 * with the bill's own commands. `RESTAURANT_DISPATCH_MANAGE` only — the floor
 * mounts this for that grant alone. Every tap is an idempotent command
 * (`useDispatchCommand`).
 */
export function DispatchProblemSheet({
  sequence,
  entry,
  billHandedOver = false,
  onReassign,
  onClose,
}: {
  sequence: number;
  entry: BillDispatch | undefined;
  /** The bill is CLOSED or SETTLED: an accepted bag may be ended as delivered. */
  billHandedOver?: boolean;
  /** Before pickup (a report on an accepted bag): hand it to another driver instead. */
  onReassign?: () => void;
  onClose: () => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const toast = usePrivateToast();
  const { send, busy } = useDispatchCommand();
  const [note, setNote] = useState("");
  const [arming, setArming] = useState<Arming>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /* "Now" for the status lines: the moment the sheet opened (no clock here). */
  const [openedAt] = useState(() => Date.now());

  const current = entry?.current ?? null;
  const actions = dispatchActions(entry, { billHandedOver });
  const problem = current?.problemOpen ? current.problem : null;
  const chip = dispatchChip(entry, openedAt, 0);
  const trimmedNote = note.trim().slice(0, DISPATCH_NOTE_MAX);

  const run = async <T,>(key: string, path: string, body: Record<string, unknown>, done: (r: T) => string) => {
    setFailure(null);
    try {
      const result = await send<T>(key, path, body);
      await queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
      toast.success(done(result));
      onClose();
    } catch (error) {
      setArming(null);
      if (error instanceof ApiError) {
        const known = REFUSALS[error.code as keyof typeof REFUSALS];
        setFailure(known ? t((m) => m.dispatch[known]) : errorMessage(error));
        // The bag moved under us: show its truth behind the message.
        void queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
      } else {
        setFailure(errorMessage(error));
      }
    }
  };

  const resolve = (resolution: DispatchProblemResolution) => {
    if (!current) return;
    // RETURN_TO_STORE and CANCELLED cannot be undone: a second tap (reviewer L3).
    if (
      resolution !== "RETRY" &&
      (arming?.kind !== "resolve" || arming.resolution !== resolution)
    ) {
      setArming({ kind: "resolve", resolution });
      return;
    }
    const name = isolate(current.driverName);
    void run<DispatchProblemResolveResponse>(
      `resolve:${current.id}:${resolution}:${trimmedNote}`,
      endpoints.restaurant.dispatchResolveProblem(current.id),
      { resolution, note: trimmedNote || null },
      () =>
        resolution === "RETRY"
          ? t((m) => m.dispatch.resolveDoneRetry, { name })
          : resolution === "RETURN_TO_STORE"
            ? t((m) => m.dispatch.resolveDoneReturn, { name })
            : t((m) => m.dispatch.resolveDoneCancel, { sequence }),
    );
  };

  const end = (outcome: DispatchEndOutcome) => {
    if (!current) return;
    if (arming?.kind !== "end" || arming.outcome !== outcome) {
      setArming({ kind: "end", outcome });
      return;
    }
    // The note only rides along while a report is open (G10 L1).
    const withNote = problem && trimmedNote ? { note: trimmedNote } : {};
    void run<DispatchAssignmentCommandResponse>(
      `end:${current.id}:${outcome}:${problem ? trimmedNote : ""}`,
      endpoints.restaurant.dispatchEnd(current.id),
      { outcome, ...withNote },
      () => t((m) => m.dispatch.endDone, { sequence }),
    );
  };

  const isBusy = busy !== null;

  return (
    <Modal
      isOpen
      onClose={isBusy ? () => undefined : onClose}
      size="sm"
      title={t((m) => m.dispatch.problemSheetTitle, { sequence })}
      closeLabel={t((m) => m.common.close)}
      data-testid="dispatch-problem-sheet"
      footerActions={
        <Button
          variant="ghost"
          size="touch"
          disabled={isBusy}
          onClick={arming ? () => setArming(null) : onClose}
          data-testid="dispatch-problem-close"
        >
          {arming ? t((m) => m.common.cancel) : t((m) => m.common.close)}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {chip ? <DispatchStatusChip chip={chip} testId="dispatch-problem-status" /> : null}

        {failure ? (
          <p
            role="alert"
            className="rounded-[var(--radius-list)] bg-[color:var(--danger-100)] px-3 py-2 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--danger)]"
            data-testid="dispatch-problem-error"
          >
            {failure}
          </p>
        ) : null}

        {!current ? (
          <p className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
            {t((m) => m.dispatch.errorNotFound)}
          </p>
        ) : null}

        {current && problem ? (
          <section
            className="flex flex-col gap-2 rounded-[var(--radius-list)] border border-[color:var(--danger)] px-3 py-2"
            data-testid="dispatch-problem-report"
          >
            <p className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
              {t((m) => m.dispatch.problemFrom, {
                name: isolate(current.driverName),
                time: clockTime(problem.reportedAt, locale),
              })}
            </p>
            <p
              className="text-[length:var(--font-size-sm)] font-extrabold text-[color:var(--text)]"
              data-testid="dispatch-problem-reason"
            >
              {t((m) => m.dispatch[REASON_KEY[problemReason(problem.reason)]])}
            </p>
            {problem.note ? (
              <p className="text-[length:var(--font-size-xs)] font-semibold text-[color:var(--text-secondary)]">
                <bdi>{t((m) => m.dispatch.problemDriverNote, { body: problem.note ?? "" })}</bdi>
              </p>
            ) : null}
          </section>
        ) : null}

        {current && problem ? (
          <FormField
            name="dispatchNote"
            label={t((m) => m.dispatch.problemReplyLabel)}
            helperText={t((m) => m.dispatch.problemReplyHint)}
          >
            <TextInput
              name="dispatchNote"
              value={note}
              maxLength={DISPATCH_NOTE_MAX}
              dir="auto"
              disabled={isBusy}
              onChange={(event) => setNote(event.target.value)}
              data-testid="dispatch-problem-note"
            />
          </FormField>
        ) : null}

        {current && actions.resolve ? (
          <div className="flex flex-col gap-2" data-testid="dispatch-problem-resolve">
            {(
              [
                ["RETRY", "resolveRetry", "resolveRetryHint"],
                ["RETURN_TO_STORE", "resolveReturn", "resolveReturnHint"],
                ["CANCELLED", "resolveCancel", "resolveCancelHint"],
              ] as const
            ).map(([resolution, label, hint]) => {
              const armed = arming?.kind === "resolve" && arming.resolution === resolution;
              return (
                <div key={resolution} className="flex flex-col gap-1">
                  <Button
                    variant={armed ? "danger" : resolution === "RETRY" ? "primary" : "outline"}
                    size="touch"
                    disabled={isBusy || (arming !== null && !armed)}
                    isLoading={busy?.startsWith(`resolve:${current.id}:${resolution}:`)}
                    onClick={() => resolve(resolution)}
                    data-testid={`dispatch-resolve-${resolution}`}
                  >
                    {armed
                      ? resolution === "CANCELLED"
                        ? t((m) => m.dispatch.resolveConfirmCancel)
                        : t((m) => m.dispatch.resolveConfirmReturn)
                      : t((m) => m.dispatch[label])}
                  </Button>
                  <p className="text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]">
                    {t((m) => m.dispatch[hint])}
                  </p>
                </div>
              );
            })}
          </div>
        ) : null}

        {current && actions.reassign && onReassign ? (
          <Button
            variant="outline"
            size="touch"
            disabled={isBusy || arming !== null}
            onClick={onReassign}
            data-testid="dispatch-problem-reassign"
          >
            {t((m) => m.dispatch.actionChange)}
          </Button>
        ) : null}

        {current && actions.end ? (
          <section
            className="flex flex-col gap-2 border-t border-[color:var(--border-light)] pt-3"
            data-testid="dispatch-end"
          >
            <p className="text-[length:var(--font-size-xs)] font-extrabold text-[color:var(--text)]">
              {t((m) => m.dispatch.endTitle)}
            </p>
            <p className="text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]">
              {t((m) => m.dispatch.endHint)}
            </p>
            {problem ? (
              <p className="text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]" data-testid="dispatch-end-note-hint">
                {t((m) => m.dispatch.endNoteLabel)}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {(
                [
                  // A bag paid before it left brings no cash back, so nothing
                  // proves it arrived: the cashier checks with the customer.
                  // Never for a bag the driver reported coming back.
                  ...(current.status === "FAILED"
                    ? []
                    : ([["DELIVERED", "endDelivered", "endConfirmDelivered"]] as const)),
                  // Never picked up: it cannot have come back or been called
                  // off on the road — those end it as a change of driver.
                  ...(current.status === "ACCEPTED"
                    ? []
                    : ([
                        ["RETURNED", "endReturned", "endConfirmReturned"],
                        ["CANCELLED", "endCancelled", "endConfirmCancelled"],
                      ] as const)),
                ] as const
              ).map(([outcome, label, confirm]) => {
                const armed = arming?.kind === "end" && arming.outcome === outcome;
                return (
                  <Button
                    key={outcome}
                    variant={armed ? "danger" : "outline"}
                    size="touch"
                    disabled={isBusy || (arming !== null && !armed)}
                    isLoading={busy?.startsWith(`end:${current.id}:${outcome}:`)}
                    onClick={() => end(outcome)}
                    data-testid={`dispatch-end-${outcome}`}
                  >
                    {armed ? t((m) => m.dispatch[confirm]) : t((m) => m.dispatch[label])}
                  </Button>
                );
              })}
            </div>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}
